import Connections from '../../connections';
import {
  convertToRaw,
} from 'draft-js';
import {
  action, observable,
} from 'mobx';
import moment from 'moment';
import {
  createArticle, filterArticles, getArticleById, getArticles, StatusValue, updateArticle,
  updateArticleStatus, articleDeepLinkQueryUrl, uodateArticleDynamicLinkById
} from '../../axios/newsRequests';
import {
  uploadFileURL,
} from '../../axios/requests';
import {
  convertCountriesToISOCodeString,
} from '../../constants/countries';
import {
  DefaultLanguage,
} from '../../constants/languages';
import {
  getHeader,
} from '../../helperFunctions/getHeader';
import {
  translate,
} from '../../helperFunctions/translation';
import {
  uploadFile,
} from '../../helperFunctions/uploadService';
import {
  ICountry,
} from '../storeInterfaces/authInterface';
import {
  IArticle, INewsAndAnnouncementStore, FilterArticlesOptions,
} from '../storeInterfaces/newsAndAnnouncementInterface';
import { RequestStatus } from 'mobx-store';

export class NewsAndAnnouncementStore implements INewsAndAnnouncementStore {
  @observable articlesList: IArticle[] = [];

  @observable selectedArticle = {
  };
  @observable articlesDeepLinks: {[key: string]: string} = {};

  @observable loading = false;

  @observable getNewsRequestStatus: RequestStatus = RequestStatus.IDLE;

  @action public async generateDeepLink(articleId: number) {
    try {
      this.loading = true;
      const res: {shortLink: string} = await Connections.firebase.generateDeepLink(articleDeepLinkQueryUrl(articleId));
      await Connections.wadiDeglaAxios.httpPut(uodateArticleDynamicLinkById(articleId) , {dynamicLink : res.shortLink});
    } catch (error) {
      throw error;
    } finally {
      this.loading = false;
    }
  }
  @action
  public async getArticles(type: 'news' | 'announcement', liveStatus: StatusValue,
    countries: ICountry[],
    langCode: string = DefaultLanguage): Promise<any> {
    try {
      this.loading = true;
      this.getNewsRequestStatus = RequestStatus.LOADING;
      const availableCountries = convertCountriesToISOCodeString(countries);
      const params = {
        availableCountries,
        status: liveStatus,
        type,
      };
      const config = {
        ...getHeader(langCode), params,
      };

      const response = await Connections.wadiDeglaAxios.httpGet(getArticles, config);
      // this.articlesDeepLinks = await Connections.firebase.get(articlesDeepLink);
      this.articlesList = this.restructureArticlesList(response.data);
      this.getNewsRequestStatus = RequestStatus.SUCCESS;
    } catch (error) {
      this.getNewsRequestStatus = RequestStatus.FAILED;
      console.log(error);
    } finally {
      this.loading = false;
    }
  }

  @action
  public async filterArticles(options: FilterArticlesOptions): Promise<any> {
    try {
      this.loading = true;
      const availableCountries = convertCountriesToISOCodeString(options.countries);

      const params = {
        availableCountries,
        type: options.type,
        query: options.query!.length > 0 ? options.query         : null,
        categoryIds: options.categoryId  ? [options.categoryId]  : null,
        publishDate: options.publishDate ? options.publishDate   : null,
        isActive: options.isActive,
        isExpired: options.isExpired,
      };

      const config = {
        ...getHeader(options.langCode),
        params,
      };
      const response = await Connections.wadiDeglaAxios.httpGet(filterArticles, config);
      this.articlesList = this.restructureArticlesList(response.data);
    } catch (error) {
      console.log(error);
    } finally {
      this.loading = false;
    }
  }

  @action.bound
  public async getArticleById(id: number, type: 'news' | 'announcement', langCode: string = DefaultLanguage): Promise<any> {
    try {
      this.loading = true;
      const response = await Connections.wadiDeglaAxios.httpGet(getArticleById(id, type, true), getHeader(langCode));
      this.selectedArticle = this.restructureArticle(response.data.article);
    } catch (error) {
      console.log(error);
    } finally {
      this.loading = false;
    }
  }

  @action.bound
  public async createArticle(newArticle: any, type: 'news' | 'announcement', langCode: string = DefaultLanguage): Promise<any> {
    return new Promise<void>(async (res, rej) => {
      try {
        const images = await this.uploadArticleImages(newArticle.images);
        const singleImage = await this.uploadArticleImage(newArticle.image);
        const videos = await this.uploadArticleVideo(newArticle.video);
        newArticle.image = singleImage;
        newArticle.images = images;
        newArticle.video = videos;
        const response = await Connections.wadiDeglaAxios.httpPost(createArticle, this.restructureCreateArticle(newArticle, type), getHeader(langCode));
        res(response.data.articleId);
      } catch (error) {
        rej(error);
      }
    });
  }

  @action.bound
  public async updateArticle(updatedArticle: any, articleId: number, type: 'news' | 'announcement',
    initGallery: {imageUrl: string; key: number}[], langCode: string = DefaultLanguage): Promise<any> {
    try {
      const singleImage = await this.uploadArticleImage(updatedArticle.image);
      const deletedImagesIds: number[] = [];
      const newImages: any[] = [];
      for (const image of updatedArticle.images) {
        if (image.preview) {
          newImages.push(image);
        } else {
          const findIndex = initGallery.findIndex((media) => media.key === image.key);
          if (findIndex !== -1) {
            initGallery.splice(findIndex, 1);
          }
        }
      }
      for (const image of initGallery) {
        deletedImagesIds.push(image.key);
      }
      const uploadedImages = await this.uploadArticleImages(newImages);
      const videos = await this.uploadArticleVideo(updatedArticle.video);
      updatedArticle.newImages = uploadedImages;
      updatedArticle.deletedImagesIds = deletedImagesIds;
      updatedArticle.image = singleImage;
      updatedArticle.video = videos;
      await Connections.wadiDeglaAxios.httpPut(updateArticle(articleId), this.restructureUpdateArticle(updatedArticle, type),
        getHeader(langCode));
    } catch (error) {
      console.log(error);
    }
  }

  @action.bound
  public async updateArticleStatus(updatedArticleStatus: boolean, index: number): Promise<any> {
    try {
      await Connections.wadiDeglaAxios.httpPut(updateArticleStatus(this.articlesList[index].articleId),
        {
          isActive: updatedArticleStatus,
        });
    } catch (error) {
      console.log(error);
    } finally {
      this.articlesList[index].isActive = updatedArticleStatus;
    }
  }

  private restructureArticlesList(articles: any[]): any[] {
    return articles.map((article: IArticle) => ({
      ...article,
      articleCategory: article.articleCategory ? article.articleCategory.title : '',
      startDate: moment(article.startDate).format('YYYY-MM-DD'),
      endDate: moment(article.endDate).format('YYYY-MM-DD'),
      edit: translate('newsListEditButton', {
      }),
      deepLink: article.dynamicLink ?  translate('newCopyDynamicLink') : translate('newGenerateDynamicLink'),
      isPublished: article.isPublished.toString().toUpperCase(),
    }));
  }

  private restructureArticle(article: IArticle): any {
    return {
      articleId: article.articleId,
      image: (article.imageURL ? [{
        imageUrl: article.imageURL, key: 1, type: 'image',
      }] : []),
      images: (article.articleImages && article.articleImages.length
        ? article.articleImages.map((image: any, index: number) => ({
          imageUrl: image.gallery.imageURL, key: image.gallery.imageId,
        })) : []),
      video: (article.videoURL ? [{
        videoUrl: article.videoURL, key: 1, type: 'video',
      }] : []),
      title: (article.title) ? article.title : '',
      preview: (article.preview) ? article.preview : '',
      description: (article.body) ? JSON.parse(article.body) : '',
      articleCategory: (article.articleCategory ? article.articleCategory.articleCategoryId : null),
      startDate: article.startDate ? new Date(article.startDate) : null,
      endDate: article.endDate ? new Date(article.endDate) : null,
      isActive: article.isActive,
    };
  }

  private restructureUpdateArticle(article: any, type: 'news' | 'announcement'): any {
    return {
      title: article.title,
      preview: article.preview,
      body: JSON.stringify(convertToRaw(article.description.getCurrentContent())),
      isActive: article.isActive,
      type,
      startDate: (article.startDate ? new Date(article.startDate).getTime() : null),
      endDate: (article.endDate ? new Date(article.endDate).getTime() : null),
      categoryId: article.articleCategory,
      videoURL: (article.video[0] ? article.video[0].videoUrl : null),
      imageURL: (article.image[0] ? article.image[0].imageUrl : null),
      newImages: article.newImages,
      deletedImagesIds: article.deletedImagesIds,
      availableCountries: article.availableCountries,
    };
  }

  private restructureCreateArticle(article: any, type: 'news' | 'announcement'): any {
    return {
      title: article.title,
      preview: article.preview,
      body: JSON.stringify(convertToRaw(article.description.getCurrentContent())),
      isActive: article.isActive,
      type,
      startDate: (article.startDate ? new Date(article.startDate).getTime() : null),
      endDate: (article.endDate ?  new Date(article.endDate).getTime() : null),
      categoryId: article.articleCategory,
      videoURL: (article.video[0] ? article.video[0].videoUrl : null),
      imageURL: (article.image[0] ? article.image[0].imageUrl : null),
      images: article.images,
      availableCountries: article.availableCountries,
    };
  }

  private uploadArticleImage(images: any[]) {
    const promises = [];
    const allImages: any[] = [];
    for (const image of images) {
      if (image.preview) {
        promises.push(
          uploadFile(uploadFileURL, image)
            .then((res: any) => {
              allImages.push({
                imageUrl: res.data.downloadURL,
              });
            }),
        );
      } else {
        allImages.push(image);
      }
    }
    return Promise.all(promises)
      .then(() => {
        console.log('done');
        return allImages;
      }).catch((error) => {
        console.log('error');
        return allImages;
      });
  }

  private uploadArticleVideo(videos: any[]) {
    const promises = [];
    const allVideos: any[] = [];
    for (const video of videos) {
      if (video.preview) {
        promises.push(
          uploadFile(uploadFileURL, video)
            .then((res: any) => {
              allVideos.push({
                videoUrl: res.data.downloadURL,
              });
            }),
        );
      } else {
        allVideos.push(video);
      }
    }
    return Promise.all(promises)
      .then(() => {
        console.log('done');
        return allVideos;
      }).catch((error) => {
        console.log('error');
        return allVideos;
      });
  }

  private uploadArticleImages(images: any[]) {
    const promises = [];
    const allImages: any[] = [];
    for (const image of images) {
      if (image.preview) {
        promises.push(
          uploadFile(uploadFileURL, image)
            .then((res: any) => {
              allImages.push(res.data.downloadURL);
            }),
        );
      } else {
        allImages.push(image.imageUrl);
      }
    }
    return Promise.all(promises)
      .then(() => {
        console.log('done');
        return allImages;
      }).catch((error) => {
        console.log('error');
        return allImages;
      });
  }
}
